import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Coffee, Search, Shield } from 'lucide-react';
import DataService from '../services/dataService';
import StorageService from '../services/storageService';
import { getDeviceInfo, getDeviceId, getDeviceName } from '../utils/deviceInfo';
import PWAInstallIcon from '../components/PWAInstallIcon';
import LogoLoader from '../components/LogoLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login illustration URL
const LOGIN_ILLUSTRATION = "https://static.prod-images.emergentagent.com/jobs/b4e419ab-e302-44b7-9ad7-697c067738c2/images/72a49a77787c314343023db664c035199987dd279cd6b5462d941d42d76a6af0.png";

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [franchiseId, setFranchiseId] = useState('');
  const [franchisePin, setFranchisePin] = useState('');
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      await fetchFranchises();
      setInitialLoading(false);
    };
    loadData();
  }, []);

  const fetchFranchises = async () => {
    try {
      const response = await axios.get(`${API}/franchises`);
      setFranchises(response.data.franchises || []);
    } catch (error) {
      console.error('Error fetching franchises:', error);
    }
  };

  const handleFranchiseLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/franchise-login`, {
        franchise_id: franchiseId,
        pin: franchisePin
      });
      if (response.data.success) {
        setUser(response.data);
        toast.success('Franchise login successful!');
        
        // Initialize cache with franchise data in background
        const franchiseData = response.data.franchise;
        
        // Save franchise info to IndexedDB
        await StorageService.saveFranchiseInfo(franchiseData);
        
        // Check session and send device info
        const deviceId = getDeviceId();
        const deviceName = getDeviceName();
        const deviceInfo = getDeviceInfo();
        
        try {
          await axios.post(`${API}/auth/check-session`, {
            franchise_id: franchiseData.id,
            device_id: deviceId,
            device_name: deviceName,
            device_info: deviceInfo
          });
          console.log('[Login] Session registered with device info');
        } catch (sessionError) {
          console.warn('[Login] Session registration failed:', sessionError);
        }
        
        // Initialize data cache (menu items, ledger parties)
        DataService.initializeCache(franchiseData.id).then(success => {
          if (success) {
            console.log('[Login] Data cache initialized');
            toast.success('Data ready for offline use', { duration: 2000 });
          }
        }).catch(err => {
          console.warn('[Login] Cache initialization failed:', err);
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const filteredFranchises = (franchises || []).filter(franchise => 
    franchise && (
      (franchise.name || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
      (franchise.location || '').toLowerCase().includes((searchTerm || '').toLowerCase())
    )
  );

  const handleFranchiseSelect = (franchise) => {
    setFranchiseId(franchise.id);
    setSearchTerm(`${franchise.name || ''} - ${franchise.location || ''}`);
    setShowDropdown(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-muted to-background">
      {/* Initial Loading Screen */}
      {initialLoading && (
        <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
          <LogoLoader size="lg" text="Loading..." />
        </div>
      )}
      
      {/* Login Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <LogoLoader size="md" text="Logging in..." />
        </div>
      )}
      
      {/* Admin Login Button - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          onClick={() => navigate('/admin-login')}
          className="bg-white text-primary hover:bg-gray-100 rounded-full px-6 py-2 font-bold uppercase tracking-wide shadow-lg flex items-center gap-2"
        >
          <Shield className="h-4 w-4" />
          Admin Login
        </Button>
      </div>

      {/* Main Container - Split Layout for Desktop/Tablet */}
      <div className="w-full max-w-5xl flex items-center justify-center gap-8 lg:gap-12">
        
        {/* Illustration - Hidden on Mobile, Visible on md+ */}
        <div className="hidden md:flex flex-1 items-center justify-center p-4">
          <div className="relative w-full max-w-lg">
            {/* Decorative background blob */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#3E2723]/5 to-[#8D6E63]/10 rounded-3xl transform rotate-3" />
            <div className="relative">
              <img 
                src={LOGIN_ILLUSTRATION} 
                alt="Coffee Franchise Billing System" 
                className="w-full h-auto object-contain drop-shadow-xl rounded-2xl"
              />
              {/* Brand tagline */}
              <div className="mt-6 text-center">
                <h2 className="text-xl lg:text-2xl font-bold text-primary">
                  Simplify Your Franchise Operations
                </h2>
                <p className="text-secondary mt-2 text-sm lg:text-base">
                  Billing • Inventory • Reports • All in One Place
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 flex-shrink-0" data-testid="login-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src="/logo.png" alt="1946 Logo" className="h-24 w-auto object-contain" />
            </div>
            <CardDescription className="text-base mt-2">Franchise Billing System</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFranchiseLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="franchise-search" className="text-sm font-medium">Search & Select Franchise</Label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-5 w-5 text-secondary z-10" />
                    <Input
                      id="franchise-search"
                      data-testid="franchise-search-input"
                      type="text"
                      placeholder="Search by franchise name or city..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                        if (e.target.value !== searchTerm) {
                          setFranchiseId('');
                        }
                      }}
                      onFocus={() => setShowDropdown(true)}
                      onBlur={() => {
                        setTimeout(() => setShowDropdown(false), 200);
                      }}
                      className="h-12 pl-10 rounded-xl border-2 focus:border-accent"
                      required
                    />
                  </div>
                  
                  {showDropdown && filteredFranchises.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-border rounded-xl shadow-lg max-h-60 overflow-y-auto z-50">
                      {filteredFranchises.map((franchise) => (
                        <div
                          key={franchise.id}
                          data-testid={`franchise-option-${franchise.id}`}
                          className="p-4 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 transition-colors"
                          onClick={() => handleFranchiseSelect(franchise)}
                        >
                          <div className="font-bold text-primary text-base">{franchise.name}</div>
                          {franchise.location && (
                            <div className="text-sm text-secondary mt-1">{franchise.location}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="franchise-pin" className="text-sm font-medium">Enter PIN</Label>
                <Input
                  id="franchise-pin"
                  data-testid="franchise-pin-input"
                  type="password"
                  placeholder="Enter 5-digit PIN"
                  value={franchisePin}
                  onChange={(e) => setFranchisePin(e.target.value)}
                  className="h-12 rounded-xl border-2 focus:border-accent"
                  maxLength="5"
                  required
                />
              </div>
              
              <Button
                type="submit"
                data-testid="franchise-login-button"
                disabled={loading || !franchiseId || !franchisePin}
                className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full text-base font-bold uppercase tracking-wider mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Logging in...' : 'Login as Franchise'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      
      {/* PWA Install Icon - Bottom Right */}
      <PWAInstallIcon />
    </div>
  );
};

export default Login;
